import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchFeatures } from "../../utils/fetchFeatures";
import { jwtDecode } from "jwt-decode";
import LoopIcon from "@mui/icons-material/Loop";
import ButtonBack from "../../utils/ButtonBack";
import CloseIcon from "@mui/icons-material/Close";
import AddPartner from "../AddPartner";
import { PhoneInput } from "react-international-phone";
import { getCountryCallingCode } from "react-phone-number-input";
import CountrySelect from "../../utils/countrySelect";
import AddInventory from "../AddInventory";
import { useOrganization } from "../../utils/OrganizationContext";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ThemedDatePicker from "../../utils/ThemedDatePicker";
import ThemedSelect from "../../utils/ThemedSelect";
import ErrorModal from "../../utils/ErrorModal";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#DDE4EC";
const FIELD_BG = "#F8FAFC";
const PRIMARY = "#0B5C8A";
const PRIMARY_DARK = "#084767";
const ERROR = "#C42B24";

// Section accent dots (multi-hue stages)
const SEC_COLORS = {
  contact: "#2F7FD1",
  assignment: "#12A08F",
  requirement: "#E0911B",
  followup: "#6D5DE8",
  attribution: "#D6457A",
  custom: "#8795A8",
};

// Scoped theme: every MUI Select / Autocomplete / Checkbox / menu on this
// form picks up the Direction C look without per-element overrides.
const formTheme = createTheme({
  palette: {
    primary: { main: PRIMARY, dark: PRIMARY_DARK, contrastText: "#FFFFFF" },
    error: { main: ERROR },
    text: { primary: INK, secondary: "#4A5A6E" },
  },
  shape: { borderRadius: 8 },
  typography: { fontFamily: FONT, button: { textTransform: "none", fontWeight: 600 } },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          background: FIELD_BG,
          fontFamily: FONT,
          fontSize: 13.5,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: FIELD_EDGE },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#B4C6DA" },
          "&.Mui-focused": { background: "#FFFFFF" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY, borderWidth: 1 },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: ERROR },
        },
        input: { "&::placeholder": { color: "#8A97A8", opacity: 1 } },
      },
    },
    MuiSvgIcon: { styleOverrides: { root: { "&.MuiSelect-icon, &.MuiAutocomplete-popupIndicator svg": { fontSize: 18, color: "#8A97A8" } } } },
    MuiFormHelperText: { styleOverrides: { root: { display: "none" } } },
    MuiMenu: {
      styleOverrides: {
        paper: { marginTop: 4, borderRadius: 12, border: `1px solid ${LINE}`, boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)" },
        list: { padding: 4 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: 13.5,
          borderRadius: 8,
          minHeight: 36,
          "&.Mui-selected, &.Mui-selected:hover": { background: "#EAF2FB" },
        },
      },
    },
    MuiListItemText: { styleOverrides: { primary: { fontFamily: FONT, fontSize: 13.5 } } },
    MuiCheckbox: { styleOverrides: { root: { color: "#C3CDD9", padding: 6, "&.Mui-checked": { color: PRIMARY } } } },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { marginTop: 4, borderRadius: 12, border: `1px solid ${LINE}`, boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)" },
        option: { fontFamily: FONT, fontSize: 13.5, borderRadius: 8, marginInline: 6 },
        groupLabel: { fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: MUTED, lineHeight: "30px" },
      },
    },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 18, boxShadow: "0 32px 64px -20px rgba(15,27,42,0.5)" } } },
    MuiBackdrop: { styleOverrides: { root: { "&:not(.MuiBackdrop-invisible)": { background: "rgba(15,27,42,0.46)" } } } },
  },
});

/* ---------- shared visual styles (values only — no logic) ---------- */
const card = {
  background: "#FFFFFF",
  border: `1px solid ${LINE}`,
  borderRadius: 14,
  marginBottom: 14,
  scrollMarginTop: 90,
};
const cardHead = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "13px 16px",
  borderBottom: `1px solid ${LINE}`,
};
const cardTitle = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  fontFamily: FONT,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.11em",
  textTransform: "uppercase",
  color: INK_2,
};
const dot = (c, size = 6) => ({
  width: size,
  height: size,
  flex: `0 0 ${size}px`,
  borderRadius: "50%",
  background: c,
});
const cardMeta = { fontFamily: FONT, fontSize: 12, color: MUTED };
const lbl = {
  display: "block",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  marginBottom: 6,
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 600,
  color: INK,
};
const req = { color: ERROR, marginLeft: 3 };
const inp = (err) => ({
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  height: 36,
  padding: "0 12px",
  margin: 0,
  background: FIELD_BG,
  border: `1px solid ${err ? ERROR : FIELD_EDGE}`,
  borderRadius: 8,
  fontFamily: FONT,
  fontSize: 13.5,
  color: INK,
  outline: "none",
});
const errText = {
  display: "block",
  margin: "4px 0 0",
  fontFamily: FONT,
  fontSize: 11.5,
  fontWeight: 500,
  color: ERROR,
};
const acSx = {
  margin: 0,
  "& .MuiInputBase-root": {
    minHeight: 36,
    fontSize: "13.5px",
    borderRadius: "8px",
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    paddingLeft: "8px !important",
  },
  "& .MuiInputBase-input": { padding: "7px 4px !important", minWidth: "0 !important" },
};
const selSx = {
  display: "flex",
  minHeight: 36,
  borderRadius: "8px",
  fontSize: "13.5px",
  margin: 0,
  background: FIELD_BG,
  "& .MuiSelect-select": {
    padding: "7px 12px",
    minHeight: "unset !important",
  },
};
const boxWrap = (err) => ({
  display: "flex",
  alignItems: "center",
  height: 36,
  boxSizing: "border-box",
  background: FIELD_BG,
  border: `1px solid ${err ? ERROR : FIELD_EDGE}`,
  borderRadius: 8,
  overflow: "hidden",
});
const prefix = {
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  height: "100%",
  padding: "0 2px 0 12px",
  fontFamily: MONO,
  fontSize: 13,
  color: MUTED,
};
const bareInp = {
  flex: 1,
  minWidth: 0,
  height: "100%",
  boxSizing: "border-box",
  padding: "0 12px 0 8px",
  margin: 0,
  border: 0,
  background: "transparent",
  fontFamily: MONO,
  fontSize: 13,
  color: INK,
  outline: "none",
};
const waBox = (on) => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  height: 36,
  boxSizing: "border-box",
  padding: "0 12px",
  background: on ? "#E9F7EE" : FIELD_BG,
  border: `1px solid ${on ? "#C4E8D2" : FIELD_EDGE}`,
  borderRadius: 8,
  cursor: "pointer",
  fontFamily: FONT,
  fontSize: 13.5,
  fontWeight: on ? 600 : 500,
  color: on ? "#1B6B41" : INK_2,
  userSelect: "none",
});
const waTick = (on) => ({
  width: 14,
  height: 14,
  flex: "0 0 14px",
  borderRadius: 3,
  boxSizing: "border-box",
  border: `1.5px solid ${on ? "#2FA864" : "#9AA7B8"}`,
  background: on ? "#2FA864" : "#FFFFFF",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#FFFFFF",
  fontSize: 10,
  fontWeight: 800,
  lineHeight: 1,
});
const primaryBtn = {
  height: 38,
  px: 2,
  margin: "0 !important",
  borderRadius: "10px !important",
  background: `${PRIMARY} !important`,
  color: "#FFFFFF !important",
  border: "0 !important",
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  whiteSpace: "nowrap",
  "&:hover": { background: `${PRIMARY_DARK} !important` },
  "&.Mui-disabled": { opacity: 0.6 },
};
const ghostBtn = {
  height: 38,
  px: 2,
  borderRadius: "10px",
  background: "#FFFFFF",
  color: INK,
  border: `1px solid ${FIELD_EDGE}`,
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 600,
  textTransform: "none",
  "&:hover": { background: "#F4F7FA" },
};
const cols = (n) => ({
  display: "grid",
  gap: "16px 12px",
  gridTemplateColumns: {
    xs: "minmax(0,1fr)",
    sm: "repeat(2, minmax(0,1fr))",
    lg: `repeat(${n}, minmax(0,1fr))`,
  },
});


// Scoped resets so global App.css rules (form cards, inputs, labels)
// can't squeeze or restyle this page.
const SCOPED_CSS = `
.dc-addlead .dc-card{width:100%!important;max-width:none!important;margin:0 0 14px 0!important;padding:0!important;box-shadow:none!important;box-sizing:border-box!important;}
.dc-addlead .dc-card label{width:auto!important;margin-top:0!important;font-size:12px!important;line-height:1.4!important;}
.dc-addlead .dc-card input:not([type=checkbox]),.dc-addlead .dc-card textarea{width:100%!important;max-width:none!important;margin:0!important;box-sizing:border-box!important;font-size:13.5px!important;}
.dc-addlead .dc-card input:not([type=checkbox]){height:36px!important;}
.dc-addlead .dc-card .MuiInputBase-root input{height:auto!important;}
.dc-addlead .dc-card input.dc-bare{height:100%!important;border:0!important;background:transparent!important;flex:1 1 auto;min-width:0;}
.dc-addlead .dc-card label.dc-wa{display:flex!important;height:36px!important;margin:0!important;font-size:13.5px!important;overflow:hidden;}
.dc-addlead .dc-card input::placeholder,.dc-addlead .dc-card textarea::placeholder{color:#8A97A8;opacity:1;}
.dc-addlead .dc-cc *{font-family:'Plus Jakarta Sans',system-ui,sans-serif!important;font-size:13.5px!important;color:#0F1B2A;}
.dc-addlead .dc-card input.dc-bare,.dc-addlead .dc-card input.dc-bare::placeholder{font-family:'Plus Jakarta Sans',system-ui,sans-serif!important;}
.dc-addlead .dc-card .MuiAutocomplete-root,.dc-addlead .dc-card .MuiInputBase-root,.dc-addlead .dc-card .MuiSelect-root{width:100%!important;max-width:none!important;}
`;

const staticFields = [
  { name: "name", label: "Lead Name", type: "text" },
  { name: "phone", label: "Phone", type: "text" },
  { name: "isWhatsapp", label: "is on WhatsApp", type: "boolean" },
  { name: "email", label: "Email", type: "email" },
  { name: "alternatePhone", label: "Alternate Phone", type: "text" },
  {
    name: "isAlternateWhatsapp",
    label: "is on WhatsApp",
    type: "boolean",
  },

  { name: "leadSource", label: "Lead Source", type: "text" },

  { name: "currentCity", label: "Current City", type: "text" },
  { name: "currentCountry", label: "Current Country", type: "text" },

  { name: "requirement", label: "Requirement", type: "text" },

  {
    name: "preferredPropertyType",
    label: "Preferred Property Type",
    type: "text",
  },

  { name: "minBudget", label: "Minimum Budget", type: "number" },
  { name: "maxBudget", label: "Maximum Budget", type: "number" },

  { name: "timelineToBuy", label: "Timeline To Buy", type: "text" },
  { name: "purpose", label: "Purpose", type: "text" },

  // DATE FIELDS
  { name: "nextFollowUp", label: "Next Follow Up", type: "date" },
  { name: "lastFollowUp", label: "Last Follow Up", type: "date" },
  { name: "remark", label: "Comment", type: "text" },

  // UTM FIELDS
  { name: "utmSource", label: "UTM Source", type: "text" },
  { name: "utmMedium", label: "UTM Medium", type: "text" },
  { name: "utmCampaign", label: "UTM Campaign", type: "text" },
  { name: "utmTerm", label: "UTM Term", type: "text" },
  { name: "utmContent", label: "UTM Content", type: "text" },

  { name: "leadScore", label: "Lead Score", type: "number" },
  { name: "aiScore", label: "AI Score", type: "number" },
];

// Placeholders from the Direction C mock (display only)
const PLACEHOLDERS = {
  name: "Full name",
  email: "name@example.com",
  requirement: "Residential — buy",
  preferredPropertyType: "2 BHK, 3 BHK…",
  timelineToBuy: "1–3 months",
  purpose: "End use",
  currentCity: "City",
  currentCountry: "Country",
  utmSource: "facebook",
  utmMedium: "cpc",
  utmCampaign: "aurum_sep_launch",
  utmTerm: "3 bhk powai",
  utmContent: "carousel_a",
};

const AddLead = () => {
  const { projectId } = useParams();
  const today = new Date().toLocaleDateString("en-CA");
  const [formData, setFormData] = useState({
    teamId: null,
    partnerId: null,
    projectId:
      projectId ||
      JSON.parse(localStorage.getItem("projectId") || "null") ||
      "",
    inventoryIds: [],
    ownedInventoryIds: [],
    assignedTo: [],
    lastFollowUp: today,
  });
  const [inputFields, setInputFields] = useState([]);
  const [leadHistory, setLeadHistory] = useState([]);
  const [projects, setProjects] = useState();
  const [partners, setPartners] = useState();
  const [inventories, setInventories] = useState();
  const [ownedInventories, setOwnedInventories] = useState();
  const [users, setUsers] = useState();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const subs = JSON.parse(localStorage.getItem("subs"));
  const [access, setAccess] = useState();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // const [role, setRole] = useState();
  const [errors, setErrors] = useState({});
  const isMobile = useMediaQuery("(max-width: 750px)");
  const hideSectionNav = useMediaQuery("(max-width: 1000px)");
  const fieldRefs = useRef({});
  const sectionRefs = useRef({});
  const [activeSection, setActiveSection] = useState("contact");
  const [openPartnerModal, setOpenPartnerModal] = useState(false);
  const [status, setStatus] = useState();
  const [sources, setSources] = useState([]);
  const [phoneCountry, setPhoneCountry] = useState("IN");
  const [altPhoneCountry, setAltPhoneCountry] = useState("IN");
  const [openInventory, setOpenInventory] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [structureMissing, setStructureMissing] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const orgId = JSON.parse(localStorage.getItem("org"));
  const orgCtx = useOrganization();
  const { role } = orgCtx;

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("error");
  const afterClose = useRef(null);

  const showModal = (msg, type = "error", onClose = null) => {
    setModalMsg(msg);
    setModalType(type);
    afterClose.current = onClose;
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    const next = afterClose.current;
    afterClose.current = null;
    if (next) next();
  };

  useEffect(() => {
    if (!projectId || !projects?.length) return;

    const hasAccess = projects.some((p) => p.projectId === projectId);

    if (!hasAccess) {
      showModal("You don't have access to this project", "error", () =>
        navigate("/"),
      );
    }
  }, [projectId, projects]);

  useEffect(() => {
    const fetchLeadStatus = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/lead-status/all?projectId=${formData?.projectId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setStatus(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchLeadStatus();

    const fetchSources = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/lead-source?projectId=${formData?.projectId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setSources(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchSources();
  }, [formData.projectId]);

  useEffect(() => {
    const getPartners = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/partner/all?orgId=${orgId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setPartners(res.data);
      } catch (error) {
        console.log(error.response.data.Exception || "Access denied");
      }
    };
    getPartners();
  }, [orgId]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        // const decoded = jwtDecode(storedUser.accessToken);
        // const userRole = decoded.userRole[0];
        // setRole(userRole);
        if (role === "ADMIN") {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/project/all?orgId=${orgId}`,
            { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
          );
          setProjects(res.data);
        } else {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/project/team/all?orgId=${orgId}`,
            { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
          );
          setProjects(res.data);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchProjects();
  }, [orgId]);

  const checkAccess = async () => {
    try {
      const hasAccess = await fetchFeatures(
        "LEAD_STRUCTURE",
        subs,
        storedUser.accessToken,
      );

      setAccess(hasAccess);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!projects?.length || !formData.projectId) {
      setUsers([]);
      return;
    }

    const selectedProject = projects.find(
      (p) => p.projectId === formData.projectId,
    );

    const projectUsers = selectedProject?.team?.orgUsers || [];

    setUsers(projectUsers);
    if (
      selectedProject?.assignmentStrategy === "DIRECT" &&
      selectedProject?.defaultAssignee
    ) {
      const defaultUser = projectUsers.find(
        (user) => user.orgUserId === selectedProject.defaultAssignee,
      );

      setFormData((prev) => ({
        ...prev,
        assignedTo: defaultUser ? [defaultUser.mobile] : null,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        assignedTo: null,
      }));
    }
  }, [projects, formData.projectId]);

  const fetchInventories = async () => {
    if (!formData.projectId) {
      setInventories([]);
      return;
    }
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/inventory/project?projectId=${formData.projectId}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setInventories(res.data || []);
    } catch (err) {
      console.error("Failed to fetch inventories", err);
    }
  };

  const fetchOwnedInventories = async () => {
    if (!formData.projectId) {
      setOwnedInventories([]);
      return;
    }
    const digitsOnly = formData.phone?.replace(/\D/g, "") || "";
    if (digitsOnly.length !== 10) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/inventory/project-and-phone?projectId=${formData.projectId}&phone=${digitsOnly}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setOwnedInventories(res.data || []);
    } catch (err) {
      console.error("Failed to fetch inventories", err);
    }
  };
  useEffect(() => {
    fetchOwnedInventories();
  }, [formData.projectId, formData.phone]);

  useEffect(() => {
    fetchInventories();
    checkAccess();
    if (subs) {
      const fetchStructure = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/lead-structure?projectId=${formData.projectId}`,
            { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
          );
          const fieldNames = res.data?.fieldNames || [];
          const customFieldsList = res.data?.customFields || [];

          if (fieldNames.length === 0 && customFieldsList.length === 0) {
            setStructureMissing(true);
            setOpen(true);
            setInputFields({ fieldNames: [], customFields: [] });
          } else {
            setStructureMissing(false);
            setOpen(false);
            setInputFields(res.data);
          }
        } catch (error) {
          console.log(error);
          setStructureMissing(true);
          setOpen(true);
          setInputFields({ fieldNames: [], customFields: [] });
        }
      };
      fetchStructure();
    }
  }, [formData.projectId]);

  const handleChange = (key, val) => {
    setFormData((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleCloseStructureModal = () => {
    setOpen(false);
    setAccess(false); // fall back to the default form shown to free users
  };

  useEffect(() => {
    if (formData.phone?.length !== 10) return;

    fetchLeadByMobile(formData.phone);
  }, [formData.phone]);

  const fetchLeadByMobile = async (phone) => {
    try {
      const res = await axios.get(
        // `${process.env.REACT_APP_BACKEND_URL}/lead/by-phone?mobile=${getCountryCallingCode(phoneCountry)}${formData.phone.replace(/\D/g, "")}`,
        `${process.env.REACT_APP_BACKEND_URL}/lead/by-phone?mobile=${formData.phone.replace(/\D/g, "")}&orgId=${orgId}`,
        {
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
        },
      );
      setLeadHistory(res.data);

      //   setFormData((prev) => ({
      //     ...prev,
      //     ...res.data,
      //   }));
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error(error);
      }
      setLeadHistory([]);
    }
  };

  const buildCustomValues = () => {
    if (!inputFields?.customFields) return [];

    return inputFields.customFields
      .map((field) => {
        const value = formData[field.fieldName];

        if (
          value === undefined ||
          value === null ||
          value === "" ||
          (Array.isArray(value) && value.length === 0)
        ) {
          return null;
        }

        // SINGLE SELECT
        if (field.type === "SINGLE_SELECT") {
          return {
            fieldName: field.fieldName,
            optionId: value,
          };
        }

        // MULTI SELECT
        if (field.type === "MULTI_SELECT") {
          return {
            fieldName: field.fieldName,
            optionIds: value,
          };
        }

        // OTHER TYPES
        return {
          fieldName: field.fieldName,
          value: value,
        };
      })
      .filter(Boolean);
  };

  const REQUIRED_FIELDS = ["name", "phone", "nextFollowUp"];

  const handlePhoneChange = (key, val) => {
    // strip anything that isn't a digit, then cap at 10 digits
    const digitsOnly = val.replace(/\D/g, "").slice(0, 10);
    handleChange(key, digitsOnly);
  };

  const handleWriteWithAI = async () => {
    try {
      setAiLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/lead/write-comment`,
        {
          leadName: formData.name,
          requirement: formData.requirement,
          preferredPropertyType: formData.preferredPropertyType,
          status: formData.status,
          existingText: formData.remark || "",
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      handleChange("remark", res.data);
    } catch (error) {
      console.log(error);
      showModal(error?.response?.data?.Exception || "Failed to generate comment");
    } finally {
      setAiLoading(false);
    }
  };

  const handleAddLead = async () => {
    const isValidEmail = (email) => {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return regex.test(email);
    };

    const isValidMobile = (phone) => {
      const regex = /^\d{7,12}$/;
      return regex.test(phone);
    };

    const newErrors = {};

    // Required fields validation
    REQUIRED_FIELDS.forEach((field) => {
      if (!formData[field] || formData[field].toString().trim() === "") {
        newErrors[field] = "This field is required";
      }
    });

    if (!formData.projectId || formData.projectId.toString().trim() === "") {
      newErrors.projectId = "This field is required";
    }

    // if (!formData.assignedTo || formData.assignedTo.length === 0) {
    //   newErrors.assignedTo = "At least select one team member";
    // }

    if (formData.email && !isValidEmail(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (formData.phone && !isValidMobile(formData.phone)) {
      newErrors.phone = "Enter a valid mobile number";
    }

    // if (formData.alternatePhone && !isValidMobile(formData.alternatePhone)) {
    //   newErrors.alternatePhone = "Enter a valid mobile number";
    // }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const FIELD_ORDER = ["name", "phone", "email", "projectId", "assignedTo"];
      const firstErrorField = FIELD_ORDER.find((f) => newErrors[f]);

      const el = fieldRefs.current[firstErrorField];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus?.();
      }

      return;
    }
    try {
      setBtnLoading(true);
      const customValues = buildCustomValues();
      const payload = {
        ...formData,
        orgId: orgId,
        assignedTo:
          formData.assignedTo?.length > 0 ? formData.assignedTo : null,
        status: formData.status,
        // phone: `${getCountryCallingCode(phoneCountry)}${formData.phone.replace(/\D/g, "")}`,
        phone: formData.phone.replace(/\D/g, ""),
        countryCode: getCountryCallingCode(phoneCountry),
        // alternatePhone: formData.alternatePhone
        //   ? `${getCountryCallingCode(altPhoneCountry)}${formData.alternatePhone.replace(/\D/g, "")}`
        //   : null,
        alternatePhone: formData.alternatePhone
          ? `${formData.alternatePhone.replace(/\D/g, "")}`
          : null,
        alternateCountryCode: formData.alternatePhone
          ? getCountryCallingCode(altPhoneCountry)
          : null,
        partnerId: formData.partnerId || null,
        partnerMobile: formData.partnerId ? null : formData.partnerInput,
        customValues,
      };
      console.log("PAYLOAD:", payload);
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/lead`, payload, {
        headers: { Authorization: `Bearer ${storedUser.accessToken}` },
      });
      navigate(
        role === "ADMIN" ? "/dashboard/leads" : "/dashboard/project-dashboard",
      );
    } catch (error) {
      console.log(error);
      showModal(error?.response?.data?.Exception || "Server Error");
    } finally {
      setBtnLoading(false);
    }
  };

  const staticFieldMap = staticFields.reduce((acc, f) => {
    acc[f.name] = f;
    return acc;
  }, {});

  const orderedFieldNames = staticFields
    .map((f) => f.name)
    .filter((name) => inputFields?.fieldNames?.includes(name));

  const handleFillForm = (lead) => {
    // e.preventDefault();
    setFormData({
      ...formData,
      name: lead.name,
      email: lead.email,
    });
  };

  const partnerOptions =
    partners
      ?.filter((partner) => partner.mobile != "")
      ?.map((p) => ({
        label:
          p.partnerName && p.partnerName.trim() !== ""
            ? p.partnerName
            : p.mobile,
        value: p.partnerId,
        mobile: p.mobile,
      })) || [];

  const enhancedOptions = [
    ...partnerOptions,
    { label: "+ Add Partner", value: "__add__" },
  ];

  useEffect(() => {
    if (users && users.length === 0) {
      setFormData((prev) => ({
        ...prev,
        assignedTo: [storedUser.username],
      }));
    }
  }, [users]);

  const sortedStatus = [...(status || [])].sort((a, b) => {
    const groupA = a.metaLeadStatus?.status || "";
    const groupB = b.metaLeadStatus?.status || "";
    if (groupA !== groupB) {
      return groupA.localeCompare(groupB);
    }
    return a.status.localeCompare(b.status);
  });

  const leadScoreOptions = [
    {
      label: "Excellent",
      options: [100, 90, 80],
    },
    {
      label: "Good",
      options: [70, 60, 50],
    },
    {
      label: "Warm",
      options: [50, 40, 30],
    },
    {
      label: "Cold",
      options: [20, 10, 0],
    },
  ].flatMap((group) =>
    group.options.map((score) => ({
      group: group.label,
      value: score,
    })),
  );

  const handleOpenInventory = () => {
    localStorage.setItem(
      "leadData",
      JSON.stringify({
        name: formData.name,
        phone: `${getCountryCallingCode(phoneCountry)}${formData.phone?.replace(/\D/g, "")}`,
      }),
    );
    setOpenInventory(true);
  };

  /* ---------- visibility (same rules as before) ---------- */
  // Free form (!access) shows the default set; structured form (access)
  // shows only fields present in the project's lead structure.
  const show = (name) => !access || orderedFieldNames.includes(name);
  // City / Country were always structure-driven in both forms.
  const showProfile = (name) => orderedFieldNames.includes(name);

  const backPath =
    role === "ADMIN" ? "/dashboard/leads" : "/dashboard/project-dashboard";
  const goBack = () => navigate(backPath);

  const selectedProject = projects?.find(
    (p) => p.projectId === formData.projectId,
  );
  const orgName =
    orgCtx?.orgName ||
    orgCtx?.organization?.orgName ||
    orgCtx?.organization?.name ||
    orgCtx?.org?.orgName ||
    "";
  const subtitle = [orgName, selectedProject?.projectName]
    .filter(Boolean)
    .join(" · ");

  /* ---------- small render helpers (markup only, same handlers) ---------- */
  const fieldBox = (key, label, required, control, err, full, boxSx) => (
    <Box key={key} sx={full ? { gridColumn: "1 / -1" } : boxSx}>
      {label && (
        <label style={lbl}>
          {label}
          {required && <span style={req}>*</span>}
        </label>
      )}
      {control}
      {err && (
        <span className="error-text" style={errText}>
          {err}
        </span>
      )}
    </Box>
  );

  const textInput = (field, withRef) =>
    fieldBox(
      field.name,
      field.label,
      REQUIRED_FIELDS.includes(field.name),
      <input
        className={`${errors[field.name] ? "input-error" : ""}`}
        ref={withRef ? (el) => (fieldRefs.current[field.name] = el) : undefined}
        type={field.type}
        placeholder={PLACEHOLDERS[field.name] || ""}
        value={formData[field.name] || ""}
        onChange={(e) => handleChange(field.name, e.target.value)}
        style={inp(!!errors[field.name])}
      />,
      errors[field.name],
    );

  const budgetInput = (field) =>
    fieldBox(
      field.name,
      field.label,
      false,
      <div style={boxWrap(false)}>
        <span style={prefix}>₹</span>
        <input
          type="number"
          value={formData[field.name] || ""}
          onChange={(e) => handleChange(field.name, e.target.value)}
          style={bareInp}
        />
      </div>,
    );

  const whatsappBox = (field) =>
    fieldBox(
      field.name,
      field.label,
      false,
      <label
        className="dc-wa"
        style={waBox(!!formData[field.name])}
      >
        <input
          type="checkbox"
          checked={!!formData[field.name]}
          onChange={(e) => handleChange(field.name, e.target.checked)}
          hidden
        />
        <span
          style={waTick(!!formData[field.name])}
        >
          {formData[field.name] ? "✓" : ""}
        </span>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Reachable on WhatsApp</span>
      </label>,
    );

  // Phone + country code in one attached field (used for Phone and
  // Alternate Phone). Same handlers / state as before.
  const phoneWithCode = (field, country, setCountry, placeholder, withRef, newRow) =>
    fieldBox(
      field.name,
      field.label,
      REQUIRED_FIELDS.includes(field.name),
      <div
        ref={withRef ? (el) => (fieldRefs.current[field.name] = el) : undefined}
        style={boxWrap(!!errors[field.name])}
      >
        <div
          className="dc-cc"
          style={{
            display: "flex",
            alignItems: "center",
            flex: "0 0 auto",
            height: "100%",
            padding: "0 6px 0 10px",
            borderRight: `1px solid ${FIELD_EDGE}`,
            boxSizing: "border-box",
          }}
        >
          <CountrySelect value={country} onChange={(c) => setCountry(c)} />
        </div>
        <input
          type="tel"
          id={field.name}
          name={field.name}
          className={`dc-inp dc-bare ${errors[field.name] ? "input-error" : ""}`}
          placeholder={placeholder}
          inputMode="numeric"
          maxLength={10}
          value={formData[field.name] || ""}
          onChange={(e) => handlePhoneChange(field.name, e.target.value)}
          style={{ ...bareInp, fontFamily: FONT, fontSize: 13.5, paddingLeft: 10 }}
        />
      </div>,
      errors[field.name],
      false,
      // Same width as the other fields; Alternate Phone starts a new row.
      newRow ? { gridColumn: { lg: "1" } } : undefined,
    );

  const dateInput = (field) =>
    fieldBox(
      field.name,
      field.label,
      REQUIRED_FIELDS.includes(field.name),
      <ThemedDatePicker
        name={field.name}
        value={formData[field.name] || ""}
        min={field.name === "nextFollowUp" ? today : undefined}
        max={field.name === "lastFollowUp" ? today : undefined}
        onChange={(e) => handleChange(field.name, e.target.value)}
        error={!!errors[field.name]}
      />,
      errors[field.name],
    );

  const commentField = () => (
    <Box key="remark" sx={{ gridColumn: "1 / -1" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.75,
        }}
      >
        <label style={{ ...lbl, marginBottom: 0 }}>Comment</label>
        <Button
          size="small"
          startIcon={<AutoFixHighIcon sx={{ fontSize: "15px !important" }} />}
          onClick={handleWriteWithAI}
          disabled={aiLoading}
          sx={{
            textTransform: "none",
            fontSize: "12px",
            minWidth: 0,
            height: 26,
            px: 1,
            borderRadius: "8px",
            color: "#4A3FBF",
            background: "#EFEDFB",
            border: "1px solid #D3D0F2",
            "&:hover": { background: "#E7E4F8" },
          }}
        >
          {aiLoading ? "Writing..." : "Write with AI"}
        </Button>
      </Box>
      <textarea
        name="remark"
        rows={3}
        className={`${errors.remark ? "input-error" : ""}`}
        value={formData.remark || ""}
        onChange={(e) => handleChange("remark", e.target.value)}
        placeholder="Enter comment..."
        style={{
          ...inp(!!errors.remark),
          height: "auto",
          minHeight: 72,
          padding: "9px 12px",
          lineHeight: 1.5,
          resize: "vertical",
        }}
      />
    </Box>
  );

  const leadScoreField = (field) =>
    fieldBox(
      field.name,
      field.label,
      false,
      <Autocomplete
        name="leadScore"
        options={leadScoreOptions}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => option.value.toString()}
        value={
          leadScoreOptions.find(
            (option) => option.value === formData[field.name],
          ) || null
        }
        onChange={(event, newValue) => {
          handleChange(field.name, newValue?.value || "");
        }}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderInput={(params) => (
          <TextField {...params} placeholder="Select Lead Score" />
        )}
        sx={acSx}
      />,
    );

  const projectField = () => (
    <Box key="projectId" ref={(el) => (fieldRefs.current.projectId = el)}>
      <label style={lbl}>
        Project<span style={req}>*</span>
      </label>
      <Autocomplete
        options={projects || []}
        getOptionLabel={(option) => option.projectName || ""}
        value={projects?.find((p) => p.projectId === formData.projectId) || null}
        onChange={(event, newValue) => {
          handleChange("projectId", newValue?.projectId || "");
        }}
        isOptionEqualToValue={(option, value) =>
          option.projectId === value.projectId
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select Project"
            error={!!errors.projectId}
            helperText={errors.projectId}
          />
        )}
        sx={acSx}
      />
      {errors.projectId && (
        <span className="error-text" style={errText}>
          {errors.projectId}
        </span>
      )}
    </Box>
  );

  const assignedField = (selfForAdminOnly) =>
    fieldBox(
      "assignedTo",
      "Assigned To",
      true,
      <Select
        className="custom-select"
        multiple
        displayEmpty
        value={formData.assignedTo || []}
        onChange={(e) => {
          const value = e.target.value;

          if (value.includes("__default__")) {
            handleChange("assignedTo", null);
            return;
          }

          handleChange("assignedTo", value);
        }}
        renderValue={(selected) => {
          if (!selected || selected.length === 0) {
            return <span style={{ color: "#8A97A8" }}>Default Assignment</span>;
          }

          return selected
            .map((value) => {
              if (
                value === storedUser.username &&
                (selfForAdminOnly ? role === "ADMIN" : true)
              ) {
                return "Self";
              }

              const usr = users?.find((user) => user.mobile === value);

              return usr?.name || value;
            })
            .join(", ");
        }}
        sx={selSx}
      >
        <MenuItem value="__default__">
          <Checkbox
            checked={!formData.assignedTo || formData.assignedTo.length === 0}
          />
          <ListItemText primary="Default Assignment" />
        </MenuItem>

        {(selfForAdminOnly ? role === "ADMIN" : true) && (
          <MenuItem value={storedUser.username}>
            <Checkbox
              checked={formData.assignedTo?.includes(storedUser.username) || false}
            />
            <ListItemText primary="Self" />
          </MenuItem>
        )}

        {users?.map((user) => (
          <MenuItem key={user.mobile} value={user.mobile}>
            <Checkbox
              checked={formData.assignedTo?.includes(user.mobile) || false}
            />

            <ListItemText primary={`${user.name} (${user.userRoleName})`} />
          </MenuItem>
        ))}
      </Select>,
      errors.assignedTo,
    );

  const statusField = () =>
    fieldBox(
      "status",
      "Status",
      false,
      <Autocomplete
        name="status"
        options={sortedStatus || []}
        getOptionLabel={(option) => option.status || ""}
        groupBy={(option) => option.metaLeadStatus.status}
        value={status?.find((p) => p.status === formData.status) || null}
        onChange={(e, value) => {
          setFormData({ ...formData, status: value?.status || "" });
        }}
        isOptionEqualToValue={(option, value) => option.status === value.status}
        renderInput={(params) => (
          <TextField {...params} placeholder="Select Status" />
        )}
        sx={acSx}
      />,
    );

  const likedField = (withClass) =>
    fieldBox(
      "inventoryIds",
      "Liked Inventory",
      false,
      <Select
        className={withClass ? "custom-select" : undefined}
        multiple
        displayEmpty
        value={formData.inventoryIds}
        onChange={(e) => handleChange("inventoryIds", e.target.value)}
        renderValue={(selected) =>
          selected.length === 0 ? (
            <span style={{ color: "#8A97A8" }}>Select inventory</span>
          ) : (
            selected
              .map((id) => {
                const inv = inventories.find((i) => i.inventoryId === id);
                return inv ? inv.type : id;
              })
              .join(", ")
          )
        }
        sx={selSx}
      >
        {inventories?.map((inv) => (
          <MenuItem key={inv.inventoryId} value={inv.inventoryId}>
            <Checkbox
              checked={formData?.inventoryIds?.includes(inv.inventoryId)}
            />
            <ListItemText
              primary={`${inv.type}${inv.floor ? ` (Floor ${inv.floor})` : ""}`}
            />
          </MenuItem>
        ))}
      </Select>,
    );

  const ownedField = () =>
    fieldBox(
      "ownedInventoryIds",
      "Owned Inventory",
      false,
      <Select
        className="custom-select"
        multiple
        displayEmpty
        value={formData.ownedInventoryIds}
        onChange={(e) => {
          const filteredValues = e.target.value.filter((v) => v !== "_add_");

          handleChange("ownedInventoryIds", filteredValues);
        }}
        renderValue={(selected) => {
          const picked = selected.filter((id) => id !== "_add_");
          if (picked.length === 0) {
            return <span style={{ color: "#8A97A8" }}>Select inventory</span>;
          }
          return picked
            .map((id) => {
              const inv = ownedInventories.find((i) => i.inventoryId === id);
              return inv ? inv.type : id;
            })
            .join(", ");
        }}
        sx={selSx}
      >
        {ownedInventories?.map((inv) => (
          <MenuItem key={inv.inventoryId} value={inv.inventoryId}>
            <Checkbox
              checked={formData?.ownedInventoryIds?.includes(inv.inventoryId)}
            />
            <ListItemText
              primary={`${inv.type}${inv.floor ? ` (Floor ${inv.floor})` : ""}`}
            />
          </MenuItem>
        ))}
        <MenuItem
          value="_add_"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleOpenInventory();
          }}
          sx={{ color: PRIMARY, fontWeight: 700, borderTop: "1px solid #EDF1F5", mt: 0.5 }}
        >
          + Add Inventory
        </MenuItem>
      </Select>,
    );

  const leadSourceField = () =>
    fieldBox(
      "leadSource",
      "Lead Source",
      false,
      <Autocomplete
        options={sources || []}
        getOptionLabel={(option) => option.source || ""}
        value={sources?.find((p) => p.source === formData.leadSource) || null}
        onChange={(event, newValue) => {
          handleChange("leadSource", newValue?.source || "");
        }}
        isOptionEqualToValue={(option, value) =>
          option.sourceId === value.sourceId
        }
        renderInput={(params) => (
          <TextField {...params} placeholder="Select Lead Source" />
        )}
        sx={acSx}
      />,
    );

  const partnerField = () =>
    fieldBox(
      "partner",
      "Partner",
      false,
      <Autocomplete
        options={enhancedOptions}
        value={formData.partnerInput || null}
        onChange={(event, newValue) => {
          if (newValue?.value === "__add__") {
            setOpenPartnerModal(true);
            return;
          }

          if (typeof newValue === "string") {
            handleChange("partnerInput", newValue);
            handleChange("partnerId", null);
          } else if (newValue) {
            handleChange("partnerInput", newValue.label);
            handleChange("partnerId", newValue.value);
          }
        }}
        onInputChange={(event, newInputValue) => {
          handleChange("partnerInput", newInputValue);
        }}
        renderInput={(params) => (
          <TextField {...params} placeholder="Select Partner" />
        )}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.value}
            style={{
              ...(props.style || {}),
              ...(option.value === "__add__"
                ? { color: PRIMARY, fontWeight: 700, borderTop: "1px solid #EDF1F5" }
                : {}),
            }}
          >
            {option.label}
          </li>
        )}
        sx={acSx}
      />,
    );

  const customFieldControl = (field) => (
    <Box key={field.fieldId}>
      <label style={lbl}>{field.label}</label>

      {/* SINGLE SELECT */}
      {field.type === "SINGLE_SELECT" && (
        <ThemedSelect
          value={formData[field.fieldName] || ""}
          onChange={(e) => handleChange(field.fieldName, e.target.value)}
          placeholder="Select"
          options={[
            { value: "", label: "Select" },
            ...(field.options
              ?.slice()
              .sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate))
              .map((opt) => ({ value: opt.optionId, label: opt.value })) || []),
          ]}
        />
      )}

      {/* MULTI SELECT */}
      {field.type === "MULTI_SELECT" && (
        <Select
          className="custom-select"
          multiple
          value={formData[field.fieldName] || []}
          onChange={(e) =>
            handleChange(
              field.fieldName,
              Array.from(e.target.selectedOptions, (o) => o.value),
            )
          }
          fullWidth
          sx={selSx}
        >
          {field.options
            ?.slice()
            .sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate))
            .map((opt) => (
              <MenuItem key={opt.optionId} value={opt.optionId}>
                {opt.value}
              </MenuItem>
            ))}
        </Select>
      )}

      {/* DATE */}
      {field.type === "DATE" && (
        <ThemedDatePicker
          value={formData[field.fieldName] || ""}
          onChange={(e) => handleChange(field.fieldName, e.target.value)}
        />
      )}

      {/* TEXT / NUMBER / BOOLEAN */}
      {!["SINGLE_SELECT", "MULTI_SELECT", "DATE"].includes(field.type) && (
        <input
          type={
            field.type === "NUMBER"
              ? "number"
              : field.type === "BOOLEAN"
                ? "checkbox"
                : "text"
          }
          value={field.type === "BOOLEAN" ? undefined : formData[field.fieldName] || ""}
          checked={
            field.type === "BOOLEAN" ? formData[field.fieldName] || false : undefined
          }
          onChange={(e) =>
            handleChange(
              field.fieldName,
              field.type === "BOOLEAN" ? e.target.checked : e.target.value,
            )
          }
          style={
            field.type === "BOOLEAN"
              ? { width: 18, height: 18, margin: "9px 0", accentColor: PRIMARY, cursor: "pointer" }
              : {
                  ...inp(false),
                  fontFamily: field.type === "NUMBER" ? MONO : FONT,
                  fontSize: field.type === "NUMBER" ? 13 : 13.5,
                }
          }
        />
      )}
    </Box>
  );

  /* ---------- section contents ---------- */
  const f = (name) => staticFieldMap[name];

  const contactItems = [
    show("name") && textInput(f("name"), true),
    show("phone") &&
      phoneWithCode(f("phone"), phoneCountry, setPhoneCountry, "Enter phone number", true),
    show("isWhatsapp") && whatsappBox(f("isWhatsapp")),
    show("email") && textInput(f("email"), true),
    show("alternatePhone") &&
      phoneWithCode(f("alternatePhone"), altPhoneCountry, setAltPhoneCountry, "Enter alternate phone", false, true),
    show("isAlternateWhatsapp") && whatsappBox(f("isAlternateWhatsapp")),
  ].filter(Boolean);

  const assignmentItems = [
    projectField(),
    assignedField(!access),
    statusField(),
    leadScoreField(f("leadScore")),
  ];

  const requirementItems = [
    show("requirement") && textInput(f("requirement")),
    show("preferredPropertyType") && textInput(f("preferredPropertyType")),
    show("minBudget") && budgetInput(f("minBudget")),
    show("maxBudget") && budgetInput(f("maxBudget")),
    show("timelineToBuy") && textInput(f("timelineToBuy")),
    show("purpose") && textInput(f("purpose")),
    showProfile("currentCity") && textInput(f("currentCity")),
    showProfile("currentCountry") && textInput(f("currentCountry")),
    likedField(!access),
    ownedField(),
  ].filter(Boolean);

  const followupItems = [
    show("nextFollowUp") && dateInput(f("nextFollowUp")),
    show("lastFollowUp") && dateInput(f("lastFollowUp")),
    show("remark") && commentField(),
  ].filter(Boolean);

  const attributionItems = [
    show("leadSource") && leadSourceField(),
    ...["utmSource", "utmMedium", "utmCampaign", "utmTerm", "utmContent"]
      .filter((n) => show(n))
      .map((n) => textInput(f(n))),
    partnerField(),
  ].filter(Boolean);

  const customItems =
    access && inputFields?.customFields?.length > 0
      ? inputFields.customFields.map(customFieldControl)
      : [];

  const sections = [
    { id: "contact", title: "Contact", items: contactItems, cols: 4, meta: `${["name", "phone"].filter(show).length} required` },
    { id: "assignment", title: "Assignment", items: assignmentItems, cols: 4, meta: "2 required" },
    { id: "requirement", title: "Requirement", items: requirementItems, cols: 5, meta: "optional" },
    {
      id: "followup",
      title: "Follow-up",
      items: followupItems,
      cols: 5,
      meta: show("nextFollowUp") ? "1 required" : "optional",
      // the Comment box doesn't count as a grid column slot visually
      count: followupItems.length,
    },
    { id: "attribution", title: "Attribution", items: attributionItems, cols: 5, meta: "optional" },
    { id: "custom", title: "Custom fields", items: customItems, cols: 5, meta: "optional" },
  ].filter((s) => s.items.length > 0);

  const sectionKey = sections.map((s) => s.id).join(",");

  // Highlight the nav item for the section in view
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) setActiveSection(en.target.dataset.sec);
        });
      },
      { rootMargin: "-25% 0px -65% 0px" },
    );
    Object.values(sectionRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [sectionKey]);

  const scrollToSection = (id) => {
    setActiveSection(id);
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderSection = (s) => (
    <section
      key={s.id}
      data-sec={s.id}
      ref={(el) => (sectionRefs.current[s.id] = el)}
      className="dc-card"
      style={card}
    >
      <div style={cardHead}>
        <span style={cardTitle}>
          <span style={dot(SEC_COLORS[s.id])} />
          {s.title}
        </span>
        <span style={cardMeta}>{s.meta}</span>
      </div>
      <Box sx={{ p: "16px 16px 18px" }}>
        <Box sx={cols(s.cols)}>{s.items}</Box>
      </Box>
    </section>
  );

  const sectionNav = (
    <nav
      style={{
        position: "sticky",
        top: 16,
        background: "#FFFFFF",
        border: `1px solid ${LINE}`,
        borderRadius: 14,
        padding: 8,
      }}
    >
      {sections.map((s) => {
        const on = activeSection === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => scrollToSection(s.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              width: "100%",
              height: 36,
              padding: "0 10px",
              marginBottom: 2,
              border: 0,
              borderRadius: 9,
              background: on ? "#EAF2FB" : "transparent",
              cursor: "pointer",
              fontFamily: FONT,
              fontSize: 13.5,
              fontWeight: on ? 700 : 500,
              color: INK,
              textAlign: "left",
            }}
          >
            <span style={dot(SEC_COLORS[s.id], 7)} />
            <span style={{ flex: 1 }}>{s.title}</span>
            <span style={{ fontFamily: MONO, fontSize: 11, color: MUTED }}>
              {s.count ?? s.items.length}
            </span>
          </button>
        );
      })}
    </nav>
  );

  const cancelButton = (
    <Button onClick={goBack} sx={ghostBtn}>
      Cancel
    </Button>
  );

  const saveButton = (
    <Button
      className="btn-contained"
      onClick={handleAddLead}
      disabled={btnLoading}
      sx={primaryBtn}
    >
      {btnLoading ? "Saving..." : "Save lead"}
    </Button>
  );

  return (
    <ThemeProvider theme={formTheme}>
      <style>{SCOPED_CSS}</style>
      <div
        className="dc-addlead"
        style={{
          fontFamily: FONT,
          color: INK,
          padding: isMobile ? "4px 0 88px" : "4px 0 32px",
        }}
      >
        {/* ---------------- PAGE HEADER ---------------- */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 2,
            mb: 2.5,
          }}
        >
          <div>
            <button
              type="button"
              onClick={goBack}
              style={{
                border: 0,
                background: "transparent",
                padding: 0,
                marginBottom: 14,
                cursor: "pointer",
                fontFamily: FONT,
                fontSize: 13,
                fontWeight: 600,
                color: PRIMARY,
              }}
            >
              ← Leads
            </button>
            <h1
              style={{
                margin: 0,
                fontFamily: FONT,
                fontSize: isMobile ? 24 : 28,
                fontWeight: 800,
                letterSpacing: "-0.03em",
                lineHeight: 1.15,
                color: INK,
              }}
            >
              Add lead
            </h1>
            {subtitle && (
              <div style={{ marginTop: 6, fontSize: 13.5, color: INK_2 }}>
                {subtitle}
              </div>
            )}
          </div>
          {!isMobile && (
            <Box sx={{ display: "flex", gap: 1.25, flex: "0 0 auto" }}>
              {cancelButton}
              {saveButton}
            </Box>
          )}
        </Box>

        {/* ---------------- NAV + SECTIONS ---------------- */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: hideSectionNav
              ? "minmax(0,1fr)"
              : "200px minmax(0,1fr)",
            gap: "14px",
            alignItems: "start",
          }}
        >
          {!hideSectionNav && sectionNav}

          <div>
            {leadHistory?.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  marginBottom: 14,
                }}
              >
                {leadHistory.map((lead) => (
                  <Box
                    key={lead.leadId}
                    className="lead-history"
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1.4fr auto" },
                      gap: 1.5,
                      alignItems: "center",
                      p: "12px 14px",
                      background: "#FDF4E1",
                      border: "1px solid #F3E0B4",
                      borderRadius: "14px",
                      fontFamily: FONT,
                      "& p": { margin: "2px 0", fontSize: 13 },
                    }}
                  >
                    <ul
                      className="lead-id"
                      style={{ margin: 0, padding: 0, listStyle: "none" }}
                    >
                      <li
                        style={{
                          fontFamily: MONO,
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: "#8A5A08",
                        }}
                      >
                        Lead ID: {lead.leadId}
                      </li>
                    </ul>
                    <div>
                      <p style={{ color: MUTED, fontWeight: 400 }}>
                        Lead Name:{" "}
                        <span style={{ fontWeight: 700, color: INK }}>{lead.name}</span>
                      </p>
                      <p style={{ color: MUTED, fontWeight: 400 }}>
                        Project Name:{" "}
                        <span style={{ fontWeight: 700, color: INK }}>
                          {lead.projectName}
                        </span>
                      </p>
                      <p style={{ color: MUTED, fontWeight: 400 }}>
                        Email ID:{" "}
                        <span style={{ fontWeight: 700, color: INK }}>{lead.email}</span>
                      </p>
                    </div>
                    <Button
                      className="btn-contained"
                      onClick={() => handleFillForm(lead)}
                      sx={{
                        ...ghostBtn,
                        marginTop: "0 !important",
                        gap: 1,
                        background: "#FFFFFF !important",
                        color: `${INK} !important`,
                        border: `1px solid ${FIELD_EDGE} !important`,
                        boxShadow: "none",
                      }}
                    >
                      <LoopIcon sx={{ fontSize: 17 }} /> Fetch details
                    </Button>
                  </Box>
                ))}
              </div>
            )}

            {sections.map(renderSection)}
          </div>
        </Box>

        {/* ---------------- MOBILE ACTION BAR ---------------- */}
        {isMobile && (
          <Box
            className="btn-container"
            sx={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 30,
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              p: "10px 16px",
              background: "#FFFFFF",
              borderTop: `1px solid ${LINE}`,
              boxShadow: "0 -8px 24px -16px rgba(15,27,42,0.3)",
            }}
          >
            <ButtonBack handleBack={goBack} />
            <Box sx={{ flex: 1, display: "flex", "& > *": { flex: 1 } }}>
              {saveButton}
            </Box>
          </Box>
        )}

        {access && structureMissing && (
          <Dialog
            open={open}
            onClose={handleCloseStructureModal}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: "18px", p: 1, textAlign: "center" } }}
          >
            <IconButton
              onClick={handleCloseStructureModal}
              sx={{
                position: "absolute",
                right: 12,
                top: 12,
                width: 30,
                height: 30,
                background: "#F4F7FA",
                border: `1px solid ${LINE}`,
                borderRadius: "9px",
                color: "#4A5A6E",
              }}
            >
              <CloseIcon sx={{ fontSize: 17 }} />
            </IconButton>

            <DialogContent sx={{ pt: 4 }}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="180px"
                gap={1.5}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: FONT,
                    fontSize: 16.5,
                    fontWeight: 800,
                    letterSpacing: "-0.015em",
                    lineHeight: 1.4,
                    color: INK,
                    mb: 1,
                    textWrap: "pretty",
                  }}
                >
                  You don't have a lead structure for this project yet. Please
                  create a structure before adding a lead.
                </Typography>

                <Button
                  className="btn-contained"
                  fullWidth
                  onClick={() => navigate("/dashboard/create-structure")}
                  sx={{ ...primaryBtn, height: 44 }}
                >
                  Create Lead Structure
                </Button>

                <Button
                  onClick={handleCloseStructureModal}
                  sx={{
                    textTransform: "none",
                    height: 40,
                    color: INK_2,
                    fontWeight: 600,
                    borderRadius: "10px",
                    "&:hover": { background: "#F4F7FA" },
                  }}
                >
                  Continue with default form
                </Button>
              </Box>
            </DialogContent>
          </Dialog>
        )}

        <Dialog
          open={openPartnerModal}
          onClose={() => setOpenPartnerModal(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "18px",
              // p: 3,
              textAlign: "center",
            },
          }}
        >
          <AddPartner />
        </Dialog>

        <Dialog
          open={openInventory}
          onClose={() => setOpenInventory(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "18px",
              // p: 3,
              textAlign: "center",
            },
          }}
        >
          <AddInventory
            onSuccess={() => {
              setOpenInventory(false);
              fetchOwnedInventories();
            }}
          />
        </Dialog>

        <ErrorModal
          open={modalOpen}
          handleClose={handleModalClose}
          msg={modalMsg}
          type={modalType}
        />
      </div>
    </ThemeProvider>
  );
};

export default AddLead;